import React from "react";
import {
  PersonaAPIResponseData,
  APILocation,
} from "../../../../../../stores/types/personasTypes.tsx";
import { RevCard, RevSlider } from "@revmatics/catalyst";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser } from "@fortawesome/pro-light-svg-icons";
import {
  genderRadioOptions,
  maritalStatusOptions,
  familySizeOptions,
  browseMethodOptions,
  buyingMethodOptions,
} from "./components/data/personaFormData.ts";
import { fullFormatNumbers } from "../../../../../../helpers/functions/formatNumber.ts";

interface DetailFieldProps {
  label: string;
  value?: string | null | number;
  isBold?: boolean;
}

const DetailField: React.FC<DetailFieldProps> = ({ label, value }) => (
  <div className="mb-4">
    <p className="typography-body-bold-sm text-monochrome-900 mb-1">{label}</p>
    <p className="typography-body-sm text-monochrome-900">{value || "N/A"}</p>
  </div>
);

const formatLocation = (loc: APILocation) =>
  `${loc.city_name}, ${loc.state}, ${loc.country}`;

const getLabelForValue = (
  value: string | number | null | undefined,
  options: { value: string | number; label: string }[],
): string => {
  if (value === null || value === undefined || value === "") {
    return "N/A";
  }

  const foundOption = options.find(
    (option) => String(option.value) === String(value),
  );

  return foundOption ? foundOption.label : String(value);
};
interface PersonaDetailViewProps {
  persona: PersonaAPIResponseData;
}

export const PersonaDetailView: React.FC<PersonaDetailViewProps> = ({
  persona,
}) => {
  const imageUrl = persona.persona_picture || persona.persona_picture_url;

  const mainCoreValue = persona.core_values?.find((v) => v.rank === 1);
  const secondaryCoreValue = persona.core_values?.find((v) => v.rank === 2);
  return (
    <div className="space-y-6">
      <RevCard className="py-4 px-6" title="General" paddingTitle="py-4 px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
          <div className="space-y-6">
            <div className="w-full sm:w-auto sm:max-w-[160px] flex-shrink-0">
              <p className="typography-body-bold-sm text-monochrome-900 mb-2">
                Persona picture
              </p>

              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt={persona.name}
                  className="h-[160px] w-[160px] rounded-md object-cover"
                />
              ) : (
                <div className="h-[160px] w-[160px] bg-monochrome-50 rounded-md flex items-center justify-center">
                  <FontAwesomeIcon
                    icon={faUser}
                    className="text-monochrome-400 text-5xl"
                  />
                </div>
              )}
            </div>
            <DetailField label="Persona name" value={persona.name} />
          </div>
          <div className="space-y-6">
            <DetailField label="Needs" value={persona.needs} />
            <DetailField label="Concerns" value={persona.concerns} />
            <DetailField label="Motivations" value={persona.motivations} />
          </div>
        </div>
      </RevCard>
      <RevCard
        className="py-4 px-6"
        title="Demographics"
        paddingTitle="py-4 px-6"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-6">
          <DetailField
            label="Age"
            value={
              persona.age_from && persona.age_to
                ? `${persona.age_from}-${persona.age_to}`
                : "N/A"
            }
          />
          <DetailField
            label="Gender"
            value={getLabelForValue(persona.gender, genderRadioOptions)}
          />
          <DetailField
            label="Income"
            value={
              persona.income_from && persona.income_to
                ? `$${fullFormatNumbers({ value: persona.income_from })} - $${fullFormatNumbers({ value: persona.income_to })}`
                : "N/A"
            }
          />
          <DetailField label="Education" value={persona.education} />
          <DetailField label="Occupation" value={persona.occupation} />
          <DetailField
            label="Marital Status"
            value={getLabelForValue(
              persona.marital_status,
              maritalStatusOptions,
            )}
          />
          <DetailField
            label="Family Size"
            value={getLabelForValue(persona.family_size, familySizeOptions)}
          />
        </div>
      </RevCard>
      <RevCard className="py-4 px-6" title="Locations" paddingTitle="py-4 px-6">
        <DetailField
          label="Include"
          value={
            persona.included_locations && persona.included_locations.length > 0
              ? (persona.included_locations as APILocation[])
                  .map(formatLocation)
                  .join("; ")
              : "None"
          }
        />
        <DetailField
          label="Exclude"
          value={
            persona.excluded_locations && persona.excluded_locations.length > 0
              ? (persona.excluded_locations as APILocation[])
                  .map(formatLocation)
                  .join("; ")
              : "None"
          }
        />
      </RevCard>

      <RevCard
        className="py-4 px-6"
        title="Psychographic Targeting"
        paddingTitle="py-4 px-6"
      >
        <DetailField
          label="Interests"
          value={
            persona.interests && persona.interests.length > 0
              ? persona.interests.join(", ")
              : "N/A"
          }
        />
        <DetailField
          label="Primary Core Value"
          value={mainCoreValue?.name || "N/A"}
        />
        <DetailField
          label="Secondary Core Value"
          value={secondaryCoreValue?.name || "N/A"}
        />
        <DetailField
          label="Preferred Browsing Method"
          value={getLabelForValue(
            persona.preferred_browsing_method,
            browseMethodOptions,
          )}
        />
        <DetailField
          label="Preferred Buying Method"
          value={getLabelForValue(
            persona.preferred_buying_method,
            buyingMethodOptions,
          )}
        />
        <p className="typography-body-bold-sm mb-3 text-monochrome-900">
          Personality Sliders
        </p>
        <RevSlider
          className="max-w-[430px] mb-2"
          disabled
          leftLabel="Introverted"
          rightLabel="Extroverted"
          value={persona.extroverted_introverted}
          name="extroverted_introverted_display"
        />
        <RevSlider
          className="max-w-[430px] mb-2"
          disabled
          leftLabel="Serious"
          rightLabel="Playful"
          value={persona.serious_playful}
          name="serious_playful_display"
        />
        <RevSlider
          className="max-w-[430px] mb-2"
          disabled
          leftLabel="Sedentary"
          rightLabel="Active"
          value={persona.active_sedentary}
          name="active_sedentary_display"
        />
        <RevSlider
          className="max-w-[430px] mb-2"
          disabled
          leftLabel="Follower"
          rightLabel="Trendsetter"
          value={persona.follower_trendsetter}
          name="follower_trendsetter_display"
        />
        <RevSlider
          className="max-w-[430px] mb-2"
          disabled
          leftLabel="Saver"
          rightLabel="Spender"
          value={persona.spender_saver}
          name="spender_saver_display"
        />
      </RevCard>
    </div>
  );
};
